import type { AvailableUnit } from '@/core/api/models/properties';
import { unitsService } from '@/core/api/services/unitsService';
import { logger } from '@/core/logger';
import {
  UNITS_NO_PRICE_LIMIT_AED,
  UNITS_PAGE_SIZE_WEB,
} from '@/modules/properties-web/hooks/useAvailableUnits';
import {
  searchUnitsByNumber,
  UNIT_SEARCH_MAX_RESULTS,
  type FilterableUnit,
} from '@/modules/properties-web/utils/unitListingFilters';
import { useEffect, useMemo, useState } from 'react';

export type SearchableUnit = FilterableUnit & Partial<AvailableUnit> & {
  unit_type?: string;
  project_name?: string;
};

/** Safety stop if the backend keeps reporting `hasNext`. */
const SEARCH_MAX_PAGES = 30;

type WalkListener = () => void;

interface CatalogueWalkState {
  units: AvailableUnit[];
  seenIds: Set<string>;
  page: number;
  complete: boolean;
  inFlight: boolean;
  listeners: Set<WalkListener>;
}

let walkState: CatalogueWalkState | null = null;

function getWalkState(): CatalogueWalkState {
  walkState ??= {
    units: [],
    seenIds: new Set(),
    page: 0,
    complete: false,
    inFlight: false,
    listeners: new Set(),
  };
  return walkState;
}

function notifyWalkListeners(): void {
  walkState?.listeners.forEach((listener) => listener());
}

function isLastPage(
  response: Readonly<{ hasNext?: boolean | string | undefined; units: AvailableUnit[] }>,
  seenBeforePage: ReadonlySet<string>,
): boolean {
  if (response.units.length === 0) {
    return true;
  }
  if (response.hasNext === false || String(response.hasNext) === 'false') {
    return true;
  }
  return !response.units.some(
    (unit) => unit?.unit_id && !seenBeforePage.has(unit.unit_id),
  );
}

/**
 * Pages GET /v1/units with the no-limit max_price until `hasNext` is false.
 * The homepage listing itself loads at 1.7M, so search cannot rely on that list alone.
 */
async function runUnfilteredCatalogueWalk(): Promise<void> {
  const state = getWalkState();
  if (state.complete || state.inFlight) {
    return;
  }
  state.inFlight = true;
  notifyWalkListeners();
  try {
    while (!state.complete && state.page < SEARCH_MAX_PAGES) {
      const seenBeforePage = new Set(state.seenIds);
      const response = await unitsService.getAvailableUnits({
        page: state.page,
        limit: UNITS_PAGE_SIZE_WEB,
        maxPrice: UNITS_NO_PRICE_LIMIT_AED,
      });
      const units = Array.isArray(response.units) ? response.units : [];
      for (const unit of units) {
        if (unit?.unit_id && !state.seenIds.has(unit.unit_id)) {
          state.seenIds.add(unit.unit_id);
          state.units.push(unit);
        }
      }
      notifyWalkListeners();
      if (isLastPage({ hasNext: response.hasNext, units }, seenBeforePage)) {
        state.complete = true;
        break;
      }
      state.page += 1;
    }
    if (!state.complete) {
      logger.warn('useUnitNumberSearch', 'Stopped paging before hasNext was false', {
        page: state.page,
      });
      state.complete = true;
    }
  } catch (error) {
    logger.error('useUnitNumberSearch', 'Unfiltered catalogue walk failed', { error });
  } finally {
    state.inFlight = false;
    notifyWalkListeners();
  }
}

function mergePresentAndRemote(
  presentUnits: readonly SearchableUnit[],
  remoteUnits: readonly AvailableUnit[],
): SearchableUnit[] {
  const merged: SearchableUnit[] = [];
  const seen = new Set<string>();
  for (const unit of presentUnits) {
    if (unit?.unit_id && !seen.has(unit.unit_id)) {
      seen.add(unit.unit_id);
      merged.push(unit);
    }
  }
  for (const unit of remoteUnits) {
    if (unit?.unit_id && !seen.has(unit.unit_id)) {
      seen.add(unit.unit_id);
      merged.push(unit);
    }
  }
  return merged;
}

/**
 * Unit-number search is independent of the price slider and bedroom chips.
 * Matches the list already on screen first, then appends hits from a full
 * unfiltered listing walk (same page/`hasNext` loop as the catalogue).
 */
export function toAvailableUnit(unit: SearchableUnit): AvailableUnit | null {
  if (
    typeof unit.unit_type !== 'string' ||
    typeof unit.number_of_bathrooms !== 'number' ||
    typeof unit.total_price !== 'number' ||
    typeof unit.total_area !== 'number' ||
    typeof unit.latitude !== 'number' ||
    typeof unit.longitude !== 'number'
  ) {
    return null;
  }
  return unit as AvailableUnit;
}

export function useUnitNumberSearch(
  query: string,
  presentUnits: readonly SearchableUnit[],
): {
  matches: SearchableUnit[];
  isSearchingRemote: boolean;
} {
  const [remoteUnits, setRemoteUnits] = useState<AvailableUnit[]>(
    () => getWalkState().units,
  );
  const [isRemoteComplete, setIsRemoteComplete] = useState(
    () => getWalkState().complete,
  );

  useEffect(() => {
    if (!query.trim()) {
      return undefined;
    }
    const onUpdate = () => {
      const state = getWalkState();
      setRemoteUnits(state.units.slice());
      setIsRemoteComplete(state.complete);
    };
    const state = getWalkState();
    state.listeners.add(onUpdate);
    onUpdate();
    void runUnfilteredCatalogueWalk();
    return () => {
      state.listeners.delete(onUpdate);
    };
  }, [query]);

  const matches = useMemo(
    () =>
      searchUnitsByNumber(
        mergePresentAndRemote(presentUnits, remoteUnits),
        query,
        UNIT_SEARCH_MAX_RESULTS,
      ),
    [presentUnits, query, remoteUnits],
  );

  return {
    matches,
    isSearchingRemote: Boolean(query.trim()) && !isRemoteComplete,
  };
}
